import {
  CommerceDecisionStatus,
  CommerceFulfillmentStatus,
  CommerceIntentStatus,
  CommerceOptionState,
  CommercePaymentActionKind,
  CommercePaymentOptionKind,
  CommerceRequirementState,
  CommerceTerminalResultKind,
  type CommerceAppointmentTimeslotSelectionDto,
  type CommerceIntentDto,
  type CommerceQuoteDto,
  type CommerceRequirementResponseDto,
  type CommerceRequestedAction,
  type ConfirmCommerceIntentDto,
  type CreateCommerceIntentDto,
  type CreateCommerceQuoteDto,
  type CreateCommerceQuoteDtoOffering,
} from "@zipper/customer-commerce-client";
import { assign, setup, type ActorRefFrom, type SnapshotFrom } from "xstate";
import { newChargeAttemptId } from "./idempotency";
import {
  cancelIntentActor,
  checkoutFailure,
  confirmIntentActor,
  createIntentActor,
  paymentActor,
  quoteActor,
  recoverIntentActor,
  resolveTargetActor,
  type CheckoutFailure,
  type CheckoutGateway,
  type CheckoutRuntimeIdentity,
  type ResolvedCheckoutTarget,
} from "./actors";

export interface CheckoutSelection {
  requestedTargetKey: string | null;
  requestedParticipantTargetKeys: readonly string[];
  participantSelectionKeys: readonly string[];
  groupKey: string | null;
  serviceKey: string | null;
  providerKey: string | null;
  timeslots: readonly CommerceAppointmentTimeslotSelectionDto[];
  accessOptionKey: string | null;
  paymentOptionKey: string | null;
  promotionCode: string | null;
  quantity: number;
  customAmountMinor: number | null;
  startsAt: string | null;
  prorationMode: string | null;
  spotKey: string | null;
}

export interface CheckoutContext {
  runtime: CheckoutRuntimeIdentity;
  gateway: CheckoutGateway;
  offering: CreateCommerceQuoteDtoOffering | null;
  requestedAction: CommerceRequestedAction | null;
  selection: CheckoutSelection;
  target: ResolvedCheckoutTarget | null;
  quote: CommerceQuoteDto | null;
  intent: CommerceIntentDto | null;
  requirementResponses: readonly CommerceRequirementResponseDto[];
  reservation: { key: string; expiresAt: string } | null;
  paymentMethodToken: string | null;
  recoveryIntentId: string | null;
  createIdempotencyKey: string;
  confirmIdempotencyKey: string;
  cancelIdempotencyKey: string;
  failure: CheckoutFailure | null;
}

export interface CheckoutMachineInput {
  runtime: CheckoutRuntimeIdentity;
  gateway: CheckoutGateway;
  offering?: CreateCommerceQuoteDtoOffering;
  requestedAction?: CommerceRequestedAction;
  requestedTargetKey?: string | null;
  quantity?: number;
  customAmountMinor?: number | null;
  timeslots?: readonly CommerceAppointmentTimeslotSelectionDto[];
  recoveryIntentId?: string | null;
}

export type CheckoutMachineOutput =
  | { status: "succeeded"; intent: CommerceIntentDto }
  | { status: "cancelled"; intent: CommerceIntentDto | null };

export type CheckoutPaymentResult =
  | { status: "succeeded"; paymentMethodToken?: string | null }
  | { status: "cancelled" }
  | { status: "failed"; message: string; retryable?: boolean };

export type CheckoutEvent =
  | { type: "RUNTIME.RECONCILED"; runtime: CheckoutRuntimeIdentity }
  | {
      type: "OFFERING.OPENED";
      offering: CreateCommerceQuoteDtoOffering;
      requestedAction: CommerceRequestedAction;
      requestedTargetKey?: string | null;
      quantity?: number;
      customAmountMinor?: number | null;
      timeslots?: readonly CommerceAppointmentTimeslotSelectionDto[];
    }
  | { type: "TARGET.CHANGED"; targetKey: string | null }
  | {
      type: "ATTENDEES.CHANGED";
      participantTargetKeys: readonly string[];
    }
  | { type: "GROUP.CHANGED"; groupKey: string | null }
  | { type: "SERVICE.CHANGED"; serviceKey: string | null }
  | { type: "PROVIDER.CHANGED"; providerKey: string | null }
  | {
      type: "TIMESLOTS.CHANGED";
      timeslots: readonly CommerceAppointmentTimeslotSelectionDto[];
    }
  | { type: "ACCESS_OPTION.CHANGED"; optionKey: string | null }
  | { type: "PAYMENT_METHOD.CHANGED"; optionKey: string | null }
  | { type: "PROMOTION.SUBMITTED"; code: string }
  | { type: "PROMOTION.REMOVED" }
  | { type: "CUSTOM_AMOUNT.CHANGED"; amountMinor: number | null }
  | {
      type: "REQUIREMENT.ANSWERED";
      response: CommerceRequirementResponseDto;
    }
  | { type: "REQUIREMENT.CLEARED"; requirementKey: string }
  | { type: "REQUIREMENT.COMPLETED" }
  | {
      type: "SPOT.SELECTED";
      spotKey: string;
      requirementKey: string;
      version: string;
    }
  | { type: "QUOTE.EXPIRED" }
  | { type: "HOLD.EXPIRED" }
  | { type: "SUBMIT" }
  | { type: "PAYMENT.PROVIDER_READY" }
  | {
      type: "PAYMENT.PROVIDER_FAILED";
      message: string;
      retryable?: boolean;
    }
  | { type: "PAYMENT.CONFIRMING" }
  | { type: "PAYMENT.RESULT"; result: CheckoutPaymentResult }
  | { type: "RETRY" }
  | { type: "CANCEL" };

const emptySelection = (): CheckoutSelection => ({
  requestedTargetKey: null,
  requestedParticipantTargetKeys: [],
  participantSelectionKeys: [],
  groupKey: null,
  serviceKey: null,
  providerKey: null,
  timeslots: [],
  accessOptionKey: null,
  paymentOptionKey: null,
  promotionCode: null,
  quantity: 1,
  customAmountMinor: null,
  startsAt: null,
  prorationMode: null,
  spotKey: null,
});

const retryableFailure = (
  code: CheckoutFailure["code"],
  message: string,
  retryable = true,
): CheckoutFailure => ({ code, message, retryable });

function selectedOptionKey(
  options: readonly { optionKey: string; state: string }[],
  current: string | null,
): string | null {
  if (
    current &&
    options.some(
      (option) =>
        option.optionKey === current &&
        option.state !== CommerceOptionState.Disabled,
    )
  ) {
    return current;
  }
  return (
    options.find((option) => option.state === CommerceOptionState.Selected)
      ?.optionKey ?? null
  );
}

function quoteHasRequiredResponses(
  quote: CommerceQuoteDto,
  responses: readonly CommerceRequirementResponseDto[],
): boolean {
  return quote.requirements.some(
    (requirement) =>
      requirement.state === CommerceRequirementState.Required &&
      !responses.some(
        (response) =>
          response.requirementKey === requirement.requirementKey &&
          response.version === requirement.version,
      ),
  );
}

function quoteNeedsConfiguration(
  quote: CommerceQuoteDto,
  selection: CheckoutSelection,
): boolean {
  const accessKey = selectedOptionKey(
    quote.accessOptions,
    selection.accessOptionKey,
  );
  const paymentKey = selectedOptionKey(
    quote.paymentOptions,
    selection.paymentOptionKey,
  );
  return (
    (quote.accessOptions.length > 0 && !accessKey) ||
    (quote.paymentOptions.length > 0 && !paymentKey)
  );
}

function runtimeAuthorityChanged(
  current: CheckoutRuntimeIdentity,
  next: CheckoutRuntimeIdentity,
): boolean {
  return (
    current.orgId !== next.orgId ||
    current.surface !== next.surface ||
    current.audience !== next.audience ||
    current.viewerRevision !== next.viewerRevision ||
    current.customer?.userId !== next.customer?.userId ||
    current.customer?.rosterId !== next.customer?.rosterId
  );
}

function quoteRequest(context: CheckoutContext): CreateCommerceQuoteDto {
  if (!context.offering || !context.requestedAction) {
    throw new Error("Cannot quote without an offering and requested action");
  }
  return {
    offering: context.offering,
    requestedAction: context.requestedAction,
    targetKey:
      context.selection.requestedTargetKey ?? context.target?.targetKey ?? null,
    ...(context.selection.requestedParticipantTargetKeys.length > 0
      ? {
          participantTargetKeys: [
            ...context.selection.requestedParticipantTargetKeys,
          ],
        }
      : {}),
    quantity: context.selection.quantity,
    selectedAccessOptionKey: context.selection.accessOptionKey,
    selectedPaymentOptionKey: context.selection.paymentOptionKey,
    timeslots: [...context.selection.timeslots],
    promotionCode: context.selection.promotionCode,
    customAmountMinor: context.selection.customAmountMinor,
    startsAt: context.selection.startsAt,
    prorationMode: context.selection.prorationMode,
  };
}

function intentRequest(context: CheckoutContext): CreateCommerceIntentDto {
  if (!context.quote)
    throw new Error("Cannot create an intent without a quote");
  const participants =
    context.selection.participantSelectionKeys.length > 0
      ? context.selection.participantSelectionKeys
      : (context.target?.participantSelectionKeys ?? []);
  return {
    quoteId: context.quote.id,
    quoteVersion: context.quote.version,
    idempotencyKey: context.createIdempotencyKey,
    targetKey:
      context.selection.requestedTargetKey ?? context.target?.targetKey ?? null,
    participantSelectionKeys: [...participants],
    accessOptionKey: context.selection.accessOptionKey,
    paymentOptionKey: context.selection.paymentOptionKey,
    requestedAction: context.requestedAction,
    requirementResponses: [...context.requirementResponses],
  };
}

function confirmRequest(context: CheckoutContext): ConfirmCommerceIntentDto {
  if (!context.intent) throw new Error("Cannot confirm without an intent");
  return {
    intentId: context.intent.id,
    intentVersion: context.intent.version,
    idempotencyKey: context.confirmIdempotencyKey,
    paymentMethodToken: context.paymentMethodToken,
  };
}

function terminalKind(intent: CommerceIntentDto): string | null {
  return intent.result?.kind ?? null;
}

/**
 * Success is deliberately stricter than truthiness or an HTTP 2xx. This guard
 * is the one path allowed to enter `succeeded`.
 */
export function isAuthoritativeCommerceSuccess(
  intent: CommerceIntentDto,
): boolean {
  const resultWithLegacyValidity = intent.result as
    | (CommerceIntentDto["result"] & { valid?: boolean })
    | null
    | undefined;
  return (
    intent.status === CommerceIntentStatus.Succeeded &&
    terminalKind(intent) === CommerceTerminalResultKind.Succeeded &&
    resultWithLegacyValidity?.valid !== false &&
    !!resultWithLegacyValidity &&
    "fulfillments" in resultWithLegacyValidity &&
    resultWithLegacyValidity.fulfillments.length > 0 &&
    resultWithLegacyValidity.fulfillments.every(
      (fulfillment) =>
        fulfillment.status === CommerceFulfillmentStatus.Succeeded &&
        typeof fulfillment.canonicalResourceKey === "string" &&
        fulfillment.canonicalResourceKey.length > 0,
    ) &&
    intent.fulfillments.length > 0 &&
    intent.fulfillments.every(
      (fulfillment) =>
        fulfillment.status === CommerceFulfillmentStatus.Succeeded &&
        typeof fulfillment.canonicalResourceKey === "string" &&
        fulfillment.canonicalResourceKey.length > 0,
    )
  );
}

function intentFailure(intent: CommerceIntentDto): CheckoutFailure {
  const result = intent.result;
  if (!result) {
    return retryableFailure(
      "invalid_terminal_result",
      "The server did not return a terminal commerce result",
      false,
    );
  }
  if (result.kind === CommerceTerminalResultKind.QuoteChanged) {
    return retryableFailure(
      "quote_changed",
      "Price or availability changed. Review the updated quote.",
    );
  }
  if (result.kind === CommerceTerminalResultKind.RecoveryRequired) {
    return retryableFailure(
      "recovery_required",
      "Payment was accepted, but fulfillment still needs recovery.",
    );
  }
  if (result.kind === CommerceTerminalResultKind.Declined) {
    return retryableFailure("declined", "The payment or booking was declined.");
  }
  if (result.kind === CommerceTerminalResultKind.Blocked) {
    return retryableFailure(
      "blocked",
      "This purchase or booking is no longer allowed.",
      false,
    );
  }
  return retryableFailure(
    "invalid_terminal_result",
    "The server returned an inconsistent commerce result",
    false,
  );
}

function quoteFailure(quote: CommerceQuoteDto): CheckoutFailure {
  if (quote.decision.status === CommerceDecisionStatus.AuthenticationRequired) {
    return retryableFailure(
      "authentication_required",
      "Sign in is required to continue.",
    );
  }
  if (quote.decision.status === CommerceDecisionStatus.Blocked) {
    return retryableFailure(
      "blocked",
      "This purchase or booking is not currently allowed.",
      false,
    );
  }
  return retryableFailure("unavailable", "The quote could not be configured.");
}

function isSelectedPaymentCollected(context: CheckoutContext): boolean {
  if (!context.quote) return true;
  const option = context.quote.paymentOptions.find(
    (candidate) => candidate.optionKey === context.selection.paymentOptionKey,
  );
  return (
    !option ||
    option.kind === CommercePaymentOptionKind.None ||
    option.kind === CommercePaymentOptionKind.SavedCard
  );
}

function clearServerDerived(): Partial<CheckoutContext> {
  return {
    quote: null,
    intent: null,
    requirementResponses: [],
    reservation: null,
    paymentMethodToken: null,
    recoveryIntentId: null,
    createIdempotencyKey: newChargeAttemptId(),
    confirmIdempotencyKey: newChargeAttemptId(),
    cancelIdempotencyKey: newChargeAttemptId(),
    failure: null,
  };
}

function rememberQuote(
  context: CheckoutContext,
  quote: CommerceQuoteDto,
): Partial<CheckoutContext> {
  const allowedActions =
    quote.decision.status === CommerceDecisionStatus.Allowed &&
    "allowedActions" in quote.decision
      ? quote.decision.allowedActions
      : [];
  const requestedAction =
    allowedActions.length > 0
      ? context.requestedAction &&
        allowedActions.includes(context.requestedAction)
        ? context.requestedAction
        : (allowedActions[0] ?? context.requestedAction)
      : context.requestedAction;
  return {
    quote,
    intent: null,
    requestedAction,
    selection: {
      ...context.selection,
      accessOptionKey: selectedOptionKey(
        quote.accessOptions,
        context.selection.accessOptionKey,
      ),
      paymentOptionKey: selectedOptionKey(
        quote.paymentOptions,
        context.selection.paymentOptionKey,
      ),
      participantSelectionKeys: quote.participants.map(
        ({ selectionKey }) => selectionKey,
      ),
    },
    failure: null,
  };
}

function upsertRequirement(
  current: readonly CommerceRequirementResponseDto[],
  response: CommerceRequirementResponseDto,
): readonly CommerceRequirementResponseDto[] {
  return [
    ...current.filter(
      (candidate) =>
        candidate.requirementKey !== response.requirementKey ||
        candidate.version !== response.version,
    ),
    response,
  ];
}

export const checkoutMachine = setup({
  types: {
    context: {} as CheckoutContext,
    events: {} as CheckoutEvent,
    input: {} as CheckoutMachineInput,
    output: {} as CheckoutMachineOutput,
  },
  actors: {
    resolveTarget: resolveTargetActor,
    quote: quoteActor,
    createIntent: createIntentActor,
    collectPayment: paymentActor,
    confirmIntent: confirmIntentActor,
    recoverIntent: recoverIntentActor,
    cancelIntent: cancelIntentActor,
  },
  guards: {
    runtimeAuthorityChanged: (
      { context },
      params: { runtime: CheckoutRuntimeIdentity },
    ) => runtimeAuthorityChanged(context.runtime, params.runtime),
    quoteAllowed: (_, params: { quote: CommerceQuoteDto }) =>
      params.quote.decision.status === CommerceDecisionStatus.Allowed,
    quoteHasRequiredResponses: (
      { context },
      params: { quote: CommerceQuoteDto },
    ) => quoteHasRequiredResponses(params.quote, context.requirementResponses),
    quoteNeedsConfiguration: (
      { context },
      params: { quote: CommerceQuoteDto },
    ) => quoteNeedsConfiguration(params.quote, context.selection),
    requirementsComplete: ({ context }) =>
      !!context.quote &&
      !quoteHasRequiredResponses(context.quote, context.requirementResponses),
    configurationComplete: ({ context }) =>
      !!context.quote &&
      !quoteNeedsConfiguration(context.quote, context.selection),
    readyToSubmit: ({ context }) =>
      !!context.quote &&
      context.quote.decision.status === CommerceDecisionStatus.Allowed &&
      !quoteHasRequiredResponses(context.quote, context.requirementResponses) &&
      !quoteNeedsConfiguration(context.quote, context.selection),
    intentSucceeded: (_, params: { intent: CommerceIntentDto }) =>
      isAuthoritativeCommerceSuccess(params.intent),
    intentActionRequired: (_, params: { intent: CommerceIntentDto }) =>
      terminalKind(params.intent) === CommerceTerminalResultKind.ActionRequired,
    intentNeedsNoPaymentConfirmation: (
      _,
      params: { intent: CommerceIntentDto },
    ) =>
      params.intent.result?.kind ===
        CommerceTerminalResultKind.ActionRequired &&
      "action" in params.intent.result &&
      params.intent.result.action.kind === CommercePaymentActionKind.None,
    intentProcessing: (_, params: { intent: CommerceIntentDto }) =>
      params.intent.status === CommerceIntentStatus.Processing ||
      terminalKind(params.intent) === CommerceTerminalResultKind.Processing,
    intentRecoveryRequired: (_, params: { intent: CommerceIntentDto }) =>
      params.intent.status === CommerceIntentStatus.RecoveryRequired ||
      terminalKind(params.intent) ===
        CommerceTerminalResultKind.RecoveryRequired,
    intentQuoteChanged: (_, params: { intent: CommerceIntentDto }) =>
      terminalKind(params.intent) === CommerceTerminalResultKind.QuoteChanged,
    intentFailed: (_, params: { intent: CommerceIntentDto }) =>
      terminalKind(params.intent) === CommerceTerminalResultKind.Declined ||
      terminalKind(params.intent) === CommerceTerminalResultKind.Blocked ||
      params.intent.status === CommerceIntentStatus.Declined ||
      params.intent.status === CommerceIntentStatus.Blocked ||
      params.intent.status === CommerceIntentStatus.Expired,
    paymentAlreadyCollected: ({ context }) =>
      isSelectedPaymentCollected(context),
    hasPaymentCollector: ({ context }) =>
      typeof context.gateway.collectPayment === "function",
    paymentSucceeded: (_, params: { result: CheckoutPaymentResult }) =>
      params.result.status === "succeeded",
    paymentProducedMethodToken: (
      _,
      params: { result: CheckoutPaymentResult },
    ) =>
      params.result.status === "succeeded" &&
      typeof params.result.paymentMethodToken === "string" &&
      params.result.paymentMethodToken.length > 0,
    actionRequiresMountedProvider: ({ context }) => {
      const result = context.intent?.result;
      return (
        result?.kind === CommerceTerminalResultKind.ActionRequired &&
        "action" in result &&
        "clientSecret" in result.action
      );
    },
    hasRecoverableIntent: ({ context }) => context.intent !== null,
    hasRecoveryIntent: ({ context }) => context.recoveryIntentId !== null,
    hasOffering: ({ context }) =>
      context.offering !== null && context.requestedAction !== null,
  },
  actions: {
    reconcileRuntime: assign({
      runtime: (_, params: { runtime: CheckoutRuntimeIdentity }) =>
        params.runtime,
    }),
    reconcileRuntimeAndInvalidate: assign(
      (
        { context },
        params: {
          runtime: CheckoutRuntimeIdentity;
        },
      ) => ({
        ...context,
        ...clearServerDerived(),
        runtime: params.runtime,
        target: null,
      }),
    ),
    openOffering: assign(
      (
        { context },
        params: {
          offering: CreateCommerceQuoteDtoOffering;
          requestedAction: CommerceRequestedAction;
          requestedTargetKey: string | null;
          quantity: number;
          customAmountMinor: number | null;
          timeslots: readonly CommerceAppointmentTimeslotSelectionDto[];
        },
      ) => ({
        ...context,
        ...clearServerDerived(),
        offering: params.offering,
        requestedAction: params.requestedAction,
        target: null,
        selection: {
          ...emptySelection(),
          requestedTargetKey: params.requestedTargetKey,
          quantity: params.quantity,
          customAmountMinor: params.customAmountMinor,
          timeslots: [...params.timeslots],
        },
      }),
    ),
    changeTarget: assign(
      ({ context }, params: { targetKey: string | null }) => ({
        ...context,
        ...clearServerDerived(),
        target: null,
        selection: {
          ...context.selection,
          requestedTargetKey: params.targetKey,
          requestedParticipantTargetKeys: params.targetKey
            ? [params.targetKey]
            : [],
          participantSelectionKeys: [],
        },
      }),
    ),
    changeAttendees: assign(
      ({ context }, params: { participantTargetKeys: readonly string[] }) => ({
        ...context,
        ...clearServerDerived(),
        selection: {
          ...context.selection,
          requestedTargetKey: params.participantTargetKeys[0] ?? null,
          requestedParticipantTargetKeys: [...params.participantTargetKeys],
          participantSelectionKeys: [],
          quantity: Math.max(1, params.participantTargetKeys.length),
        },
      }),
    ),
    changeGroup: assign(({ context }, params: { groupKey: string | null }) => ({
      ...context,
      ...clearServerDerived(),
      selection: { ...context.selection, groupKey: params.groupKey },
    })),
    changeService: assign(
      ({ context }, params: { serviceKey: string | null }) => ({
        ...context,
        ...clearServerDerived(),
        selection: { ...context.selection, serviceKey: params.serviceKey },
      }),
    ),
    changeProvider: assign(
      ({ context }, params: { providerKey: string | null }) => ({
        ...context,
        ...clearServerDerived(),
        selection: { ...context.selection, providerKey: params.providerKey },
      }),
    ),
    changeTimeslots: assign(
      (
        { context },
        params: {
          timeslots: readonly CommerceAppointmentTimeslotSelectionDto[];
        },
      ) => ({
        ...context,
        ...clearServerDerived(),
        selection: {
          ...context.selection,
          timeslots: [...params.timeslots],
          spotKey: null,
        },
      }),
    ),
    changeAccessOption: assign(
      ({ context }, params: { optionKey: string | null }) => ({
        ...context,
        ...clearServerDerived(),
        selection: {
          ...context.selection,
          accessOptionKey: params.optionKey,
          paymentOptionKey: null,
        },
      }),
    ),
    changePaymentOption: assign(
      ({ context }, params: { optionKey: string | null }) => ({
        ...context,
        ...clearServerDerived(),
        selection: {
          ...context.selection,
          paymentOptionKey: params.optionKey,
        },
      }),
    ),
    submitPromotion: assign(({ context }, params: { code: string }) => ({
      ...context,
      ...clearServerDerived(),
      selection: {
        ...context.selection,
        promotionCode: params.code.trim() || null,
      },
    })),
    removePromotion: assign(({ context }) => ({
      ...context,
      ...clearServerDerived(),
      selection: { ...context.selection, promotionCode: null },
    })),
    changeCustomAmount: assign(
      ({ context }, params: { amountMinor: number | null }) => ({
        ...context,
        ...clearServerDerived(),
        selection: {
          ...context.selection,
          customAmountMinor: params.amountMinor,
        },
      }),
    ),
    rememberTarget: assign({
      target: (_, params: { target: ResolvedCheckoutTarget }) => params.target,
      failure: null,
    }),
    rememberQuote: assign(
      ({ context }, params: { quote: CommerceQuoteDto }) => ({
        ...context,
        ...rememberQuote(context, params.quote),
      }),
    ),
    rememberQuoteFailure: assign(
      (
        { context },
        params: {
          quote: CommerceQuoteDto;
        },
      ) => ({
        ...context,
        ...rememberQuote(context, params.quote),
        failure: quoteFailure(params.quote),
      }),
    ),
    rememberActorFailure: assign({
      failure: (_, params: { error: unknown }) => checkoutFailure(params.error),
    }),
    answerRequirement: assign({
      requirementResponses: (
        { context },
        params: { response: CommerceRequirementResponseDto },
      ) => upsertRequirement(context.requirementResponses, params.response),
      failure: null,
    }),
    clearRequirement: assign({
      requirementResponses: ({ context }, params: { requirementKey: string }) =>
        context.requirementResponses.filter(
          ({ requirementKey }) => requirementKey !== params.requirementKey,
        ),
      failure: null,
    }),
    selectSpot: assign(
      (
        { context },
        params: {
          spotKey: string;
          requirementKey: string;
          version: string;
        },
      ) => ({
        ...context,
        selection: { ...context.selection, spotKey: params.spotKey },
        requirementResponses: upsertRequirement(context.requirementResponses, {
          requirementKey: params.requirementKey,
          version: params.version,
          value: { spotKey: params.spotKey },
        }),
        failure: null,
      }),
    ),
    rememberIntent: assign({
      intent: (_, params: { intent: CommerceIntentDto }) => params.intent,
      recoveryIntentId: (_, params: { intent: CommerceIntentDto }) =>
        params.intent.id,
      failure: null,
    }),
    rememberIntentFailure: assign({
      intent: (_, params: { intent: CommerceIntentDto }) => params.intent,
      recoveryIntentId: (_, params: { intent: CommerceIntentDto }) =>
        params.intent.id,
      failure: (_, params: { intent: CommerceIntentDto }) =>
        intentFailure(params.intent),
    }),
    invalidateQuoteAfterIntentChange: assign(
      (
        { context },
        params: {
          intent: CommerceIntentDto;
        },
      ) => ({
        ...context,
        ...clearServerDerived(),
        intent: params.intent,
        failure: intentFailure(params.intent),
      }),
    ),
    rememberPayment: assign({
      paymentMethodToken: (
        _,
        params: { result: { paymentMethodToken: string | null } },
      ) => params.result.paymentMethodToken,
      failure: null,
    }),
    rememberPaymentEvent: assign({
      paymentMethodToken: (_, params: { result: CheckoutPaymentResult }) =>
        params.result.status === "succeeded"
          ? (params.result.paymentMethodToken ?? null)
          : null,
      failure: null,
    }),
    rememberPaymentFailure: assign({
      failure: (_, params: { result: CheckoutPaymentResult }) =>
        params.result.status === "failed"
          ? retryableFailure(
              "payment_failed",
              params.result.message,
              params.result.retryable ?? true,
            )
          : retryableFailure(
              "payment_failed",
              "Payment collection was cancelled.",
            ),
    }),
    rememberProviderFailure: assign({
      failure: (
        _,
        params: {
          message: string;
          retryable: boolean;
        },
      ) => retryableFailure("unavailable", params.message, params.retryable),
    }),
    clearFailure: assign({ failure: null }),
  },
}).createMachine({
  id: "commerceCheckout",
  context: ({ input }) => ({
    runtime: input.runtime,
    gateway: input.gateway,
    offering: input.offering ?? null,
    requestedAction: input.requestedAction ?? null,
    selection: {
      ...emptySelection(),
      requestedTargetKey: input.requestedTargetKey ?? null,
      quantity: Math.max(1, input.quantity ?? 1),
      customAmountMinor: input.customAmountMinor ?? null,
      timeslots: [...(input.timeslots ?? [])],
    },
    target: null,
    quote: null,
    intent: null,
    requirementResponses: [],
    reservation: null,
    paymentMethodToken: null,
    recoveryIntentId: input.recoveryIntentId ?? null,
    createIdempotencyKey: newChargeAttemptId(),
    confirmIdempotencyKey: newChargeAttemptId(),
    cancelIdempotencyKey: newChargeAttemptId(),
    failure: null,
  }),
  output: ({ context }) =>
    context.intent && isAuthoritativeCommerceSuccess(context.intent)
      ? { status: "succeeded", intent: context.intent }
      : { status: "cancelled", intent: context.intent },
  initial: "idle",
  on: {
    "RUNTIME.RECONCILED": [
      {
        guard: {
          type: "runtimeAuthorityChanged",
          params: ({ event }) => ({ runtime: event.runtime }),
        },
        target: ".resolvingTarget",
        actions: {
          type: "reconcileRuntimeAndInvalidate",
          params: ({ event }) => ({ runtime: event.runtime }),
        },
      },
      {
        actions: {
          type: "reconcileRuntime",
          params: ({ event }) => ({ runtime: event.runtime }),
        },
      },
    ],
    "OFFERING.OPENED": {
      target: ".resolvingTarget",
      actions: {
        type: "openOffering",
        params: ({ event }) => ({
          offering: event.offering,
          requestedAction: event.requestedAction,
          requestedTargetKey: event.requestedTargetKey ?? null,
          quantity: Math.max(1, event.quantity ?? 1),
          customAmountMinor: event.customAmountMinor ?? null,
          timeslots: event.timeslots ?? [],
        }),
      },
    },
    "TARGET.CHANGED": {
      target: ".resolvingTarget",
      actions: {
        type: "changeTarget",
        params: ({ event }) => ({ targetKey: event.targetKey }),
      },
    },
    "ATTENDEES.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changeAttendees",
        params: ({ event }) => ({
          participantTargetKeys: event.participantTargetKeys,
        }),
      },
    },
    "GROUP.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changeGroup",
        params: ({ event }) => ({ groupKey: event.groupKey }),
      },
    },
    "SERVICE.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changeService",
        params: ({ event }) => ({ serviceKey: event.serviceKey }),
      },
    },
    "PROVIDER.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changeProvider",
        params: ({ event }) => ({ providerKey: event.providerKey }),
      },
    },
    "TIMESLOTS.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changeTimeslots",
        params: ({ event }) => ({ timeslots: event.timeslots }),
      },
    },
    "ACCESS_OPTION.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changeAccessOption",
        params: ({ event }) => ({ optionKey: event.optionKey }),
      },
    },
    "PAYMENT_METHOD.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changePaymentOption",
        params: ({ event }) => ({ optionKey: event.optionKey }),
      },
    },
    "PROMOTION.SUBMITTED": {
      target: ".quoting",
      actions: {
        type: "submitPromotion",
        params: ({ event }) => ({ code: event.code }),
      },
    },
    "PROMOTION.REMOVED": {
      target: ".quoting",
      actions: "removePromotion",
    },
    "CUSTOM_AMOUNT.CHANGED": {
      target: ".quoting",
      actions: {
        type: "changeCustomAmount",
        params: ({ event }) => ({ amountMinor: event.amountMinor }),
      },
    },
    "QUOTE.EXPIRED": {
      target: ".quoting",
      actions: "clearFailure",
    },
    "HOLD.EXPIRED": {
      target: ".quoting",
      actions: "clearFailure",
    },
    CANCEL: [
      {
        guard: "hasRecoverableIntent",
        target: ".cancelling",
      },
      {
        target: ".cancelled",
      },
    ],
  },
  states: {
    idle: {
      always: [
        {
          guard: "hasRecoveryIntent",
          target: "recovering",
        },
        {
          guard: "hasOffering",
          target: "resolvingTarget",
        },
      ],
    },
    resolvingTarget: {
      invoke: {
        src: "resolveTarget",
        input: ({ context }) => ({
          gateway: context.gateway,
          runtime: context.runtime,
          requestedTargetKey: context.selection.requestedTargetKey,
        }),
        onDone: {
          target: "quoting",
          actions: {
            type: "rememberTarget",
            params: ({ event }) => ({ target: event.output }),
          },
        },
        onError: {
          target: "failed",
          actions: {
            type: "rememberActorFailure",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    quoting: {
      invoke: {
        src: "quote",
        input: ({ context }) => ({
          gateway: context.gateway,
          runtime: context.runtime,
          request: quoteRequest(context),
        }),
        onDone: [
          {
            guard: {
              type: "quoteAllowed",
              params: ({ event }) => ({ quote: event.output }),
            },
            target: "collectingRequirements",
            actions: {
              type: "rememberQuote",
              params: ({ event }) => ({ quote: event.output }),
            },
          },
          {
            target: "failed",
            actions: {
              type: "rememberQuoteFailure",
              params: ({ event }) => ({ quote: event.output }),
            },
          },
        ],
        onError: {
          target: "failed",
          actions: {
            type: "rememberActorFailure",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    configuring: {
      always: {
        guard: "configurationComplete",
        target: "ready",
      },
    },
    collectingRequirements: {
      always: [
        {
          guard: "requirementsComplete",
          target: "configuring",
        },
      ],
      on: {
        "REQUIREMENT.ANSWERED": {
          actions: {
            type: "answerRequirement",
            params: ({ event }) => ({ response: event.response }),
          },
        },
        "REQUIREMENT.CLEARED": {
          target: "collectingRequirements",
          reenter: true,
          actions: {
            type: "clearRequirement",
            params: ({ event }) => ({
              requirementKey: event.requirementKey,
            }),
          },
        },
        "REQUIREMENT.COMPLETED": {},
        "SPOT.SELECTED": {
          actions: {
            type: "selectSpot",
            params: ({ event }) => ({
              spotKey: event.spotKey,
              requirementKey: event.requirementKey,
              version: event.version,
            }),
          },
        },
      },
    },
    ready: {
      on: {
        SUBMIT: {
          guard: "readyToSubmit",
          target: "creatingIntent",
          actions: "clearFailure",
        },
      },
    },
    creatingIntent: {
      invoke: {
        src: "createIntent",
        input: ({ context }) => ({
          gateway: context.gateway,
          runtime: context.runtime,
          request: intentRequest(context),
        }),
        onDone: [
          {
            guard: {
              type: "intentSucceeded",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "succeeded",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentNeedsNoPaymentConfirmation",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "confirming",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentActionRequired",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "awaitingExternalAction",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentProcessing",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "recovering",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentRecoveryRequired",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "recovering",
            actions: {
              type: "rememberIntentFailure",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentQuoteChanged",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "quoting",
            actions: {
              type: "invalidateQuoteAfterIntentChange",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentFailed",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "failed",
            actions: {
              type: "rememberIntentFailure",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: "paymentAlreadyCollected",
            target: "confirming",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            target: "collectingPayment",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
        ],
        onError: {
          target: "failed",
          actions: {
            type: "rememberActorFailure",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    collectingPayment: {
      initial: "deciding",
      states: {
        deciding: {
          always: [
            { guard: "hasPaymentCollector", target: "automatic" },
            { target: "waiting" },
          ],
        },
        automatic: {
          invoke: {
            src: "collectPayment",
            input: ({ context }) => ({
              gateway: context.gateway,
              runtime: context.runtime,
              intent: context.intent!,
              paymentOptionKey: context.selection.paymentOptionKey,
            }),
            onDone: {
              target: "#commerceCheckout.confirming",
              actions: {
                type: "rememberPayment",
                params: ({ event }) => ({ result: event.output }),
              },
            },
            onError: {
              target: "#commerceCheckout.failed",
              actions: {
                type: "rememberActorFailure",
                params: ({ event }) => ({ error: event.error }),
              },
            },
          },
        },
        waiting: {
          on: {
            "PAYMENT.RESULT": [
              {
                guard: {
                  type: "paymentSucceeded",
                  params: ({ event }) => ({ result: event.result }),
                },
                target: "#commerceCheckout.confirming",
                actions: {
                  type: "rememberPaymentEvent",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
              {
                target: "#commerceCheckout.failed",
                actions: {
                  type: "rememberPaymentFailure",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
            ],
          },
        },
      },
    },
    confirming: {
      invoke: {
        src: "confirmIntent",
        input: ({ context }) => ({
          gateway: context.gateway,
          runtime: context.runtime,
          request: confirmRequest(context),
        }),
        onDone: [
          {
            guard: {
              type: "intentSucceeded",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "succeeded",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentActionRequired",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "awaitingExternalAction",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentProcessing",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "recovering",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentRecoveryRequired",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "recovering",
            actions: {
              type: "rememberIntentFailure",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentQuoteChanged",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "quoting",
            actions: {
              type: "invalidateQuoteAfterIntentChange",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            target: "failed",
            actions: {
              type: "rememberIntentFailure",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
        ],
        onError: {
          target: "recovering",
          actions: {
            type: "rememberActorFailure",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    awaitingExternalAction: {
      initial: "deciding",
      states: {
        deciding: {
          always: [
            {
              guard: "actionRequiresMountedProvider",
              target: "preparingProvider",
            },
            { target: "awaitingRedirect" },
          ],
        },
        preparingProvider: {
          on: {
            "PAYMENT.PROVIDER_READY": {
              target: "providerReady",
              actions: "clearFailure",
            },
            "PAYMENT.PROVIDER_FAILED": {
              target: "providerUnavailable",
              actions: {
                type: "rememberProviderFailure",
                params: ({ event }) => ({
                  message: event.message,
                  retryable: event.retryable ?? true,
                }),
              },
            },
          },
        },
        providerReady: {
          on: {
            "PAYMENT.CONFIRMING": {
              target: "confirmingProvider",
              actions: "clearFailure",
            },
            "PAYMENT.PROVIDER_FAILED": {
              target: "providerUnavailable",
              actions: {
                type: "rememberProviderFailure",
                params: ({ event }) => ({
                  message: event.message,
                  retryable: event.retryable ?? true,
                }),
              },
            },
          },
        },
        confirmingProvider: {
          on: {
            "PAYMENT.RESULT": [
              {
                guard: {
                  type: "paymentProducedMethodToken",
                  params: ({ event }) => ({ result: event.result }),
                },
                target: "#commerceCheckout.confirming",
                actions: {
                  type: "rememberPaymentEvent",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
              {
                guard: {
                  type: "paymentSucceeded",
                  params: ({ event }) => ({ result: event.result }),
                },
                target: "#commerceCheckout.recovering",
                actions: {
                  type: "rememberPaymentEvent",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
              {
                target: "providerReady",
                actions: {
                  type: "rememberPaymentFailure",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
            ],
          },
        },
        providerUnavailable: {
          on: {
            RETRY: {
              target: "preparingProvider",
              actions: "clearFailure",
            },
          },
        },
        awaitingRedirect: {
          on: {
            "PAYMENT.RESULT": [
              {
                guard: {
                  type: "paymentProducedMethodToken",
                  params: ({ event }) => ({ result: event.result }),
                },
                target: "#commerceCheckout.confirming",
                actions: {
                  type: "rememberPaymentEvent",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
              {
                guard: {
                  type: "paymentSucceeded",
                  params: ({ event }) => ({ result: event.result }),
                },
                target: "#commerceCheckout.recovering",
                actions: {
                  type: "rememberPaymentEvent",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
              {
                target: "#commerceCheckout.failed",
                actions: {
                  type: "rememberPaymentFailure",
                  params: ({ event }) => ({ result: event.result }),
                },
              },
            ],
          },
        },
      },
    },
    cancelling: {
      invoke: {
        src: "cancelIntent",
        input: ({ context }) => ({
          gateway: context.gateway,
          runtime: context.runtime,
          request: {
            intentId: context.intent!.id,
            intentVersion: context.intent!.version,
            idempotencyKey: context.cancelIdempotencyKey,
          },
        }),
        onDone: [
          {
            guard: {
              type: "intentSucceeded",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "succeeded",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            guard: {
              type: "intentProcessing",
              params: ({ event }) => ({ intent: event.output }),
            },
            target: "recovering",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
          {
            target: "cancelled",
            actions: {
              type: "rememberIntent",
              params: ({ event }) => ({ intent: event.output }),
            },
          },
        ],
        onError: {
          target: "recovering",
          actions: {
            type: "rememberActorFailure",
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
    recovering: {
      initial: "loading",
      states: {
        loading: {
          invoke: {
            src: "recoverIntent",
            input: ({ context }) => ({
              gateway: context.gateway,
              runtime: context.runtime,
              intentId: context.intent?.id ?? context.recoveryIntentId!,
            }),
            onDone: [
              {
                guard: {
                  type: "intentSucceeded",
                  params: ({ event }) => ({ intent: event.output }),
                },
                target: "#commerceCheckout.succeeded",
                actions: {
                  type: "rememberIntent",
                  params: ({ event }) => ({ intent: event.output }),
                },
              },
              {
                guard: {
                  type: "intentNeedsNoPaymentConfirmation",
                  params: ({ event }) => ({ intent: event.output }),
                },
                target: "#commerceCheckout.confirming",
                actions: {
                  type: "rememberIntent",
                  params: ({ event }) => ({ intent: event.output }),
                },
              },
              {
                guard: {
                  type: "intentActionRequired",
                  params: ({ event }) => ({ intent: event.output }),
                },
                target: "#commerceCheckout.awaitingExternalAction",
                actions: {
                  type: "rememberIntent",
                  params: ({ event }) => ({ intent: event.output }),
                },
              },
              {
                guard: {
                  type: "intentQuoteChanged",
                  params: ({ event }) => ({ intent: event.output }),
                },
                target: "#commerceCheckout.quoting",
                actions: {
                  type: "invalidateQuoteAfterIntentChange",
                  params: ({ event }) => ({ intent: event.output }),
                },
              },
              {
                guard: {
                  type: "intentFailed",
                  params: ({ event }) => ({ intent: event.output }),
                },
                target: "#commerceCheckout.failed",
                actions: {
                  type: "rememberIntentFailure",
                  params: ({ event }) => ({ intent: event.output }),
                },
              },
              {
                target: "waiting",
                actions: {
                  type: "rememberIntent",
                  params: ({ event }) => ({ intent: event.output }),
                },
              },
            ],
            onError: {
              target: "waiting",
              actions: {
                type: "rememberActorFailure",
                params: ({ event }) => ({ error: event.error }),
              },
            },
          },
        },
        waiting: {
          // Confirmation can finish after the client actor times out. Keep
          // polling the same canonical intent instead of leaving checkout
          // stuck on "Updating…" or inviting a duplicate purchase.
          after: {
            2000: { target: "loading" },
          },
          on: {
            RETRY: {
              target: "loading",
              actions: "clearFailure",
            },
          },
        },
      },
    },
    succeeded: {
      type: "final",
    },
    failed: {
      on: {
        RETRY: [
          {
            guard: "hasRecoverableIntent",
            target: "recovering",
            actions: "clearFailure",
          },
          {
            target: "quoting",
            actions: "clearFailure",
          },
        ],
      },
    },
    cancelled: {
      type: "final",
    },
  },
});

export type CheckoutActorRef = ActorRefFrom<typeof checkoutMachine>;
export type CheckoutSnapshot = SnapshotFrom<typeof checkoutMachine>;
